import html2canvas from "html2canvas";
import { Button } from "components/DesignSystem/Button/Button";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  BusinessReview,
  useExportBusinessReviewMutation,
} from "store/apis/businessReview";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { openLink } from "utils/openLink";
import { GenerateReviewModal } from "./GenerateReviewModal";
import { UniverSlidesViewer } from "./UniverSlidesViewer";

const ALIGN_MAP = ["left", "center", "right", "justify"] as const;

const captureSlide = async (
  page: any,
  pw: number,
  ph: number
): Promise<string> => {
  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed;
    top: -99999px;
    left: -99999px;
    width: ${pw}px;
    height: ${ph}px;
    overflow: hidden;
    background: ${page.background?.rgb ?? "#0F0A1E"};
  `;

  const order: string[] =
    page.pageElementOrder ?? Object.keys(page.pageElements ?? {});
  const elems: Record<string, any> = page.pageElements ?? {};
  const sorted = [...order].sort(
    (a, b) => (elems[a]?.zIndex ?? 0) - (elems[b]?.zIndex ?? 0)
  );

  for (const id of sorted) {
    const e = elems[id];
    if (!e) continue;

    const el = document.createElement("div");
    el.style.cssText = `
      position: absolute;
      left: ${e.left}px;
      top: ${e.top}px;
      width: ${e.width}px;
      height: ${e.height}px;
      z-index: ${e.zIndex ?? 0};
    `;

    if (e.elementType === 1) {
      el.style.background =
        e.shape?.shapeProperties?.shapeBackgroundFill?.rgb ?? "transparent";
    } else if (e.elementType === 0) {
      const body = e.richText?.body ?? {};
      const ts = body.textRuns?.[0]?.ts ?? {};
      const alignIdx: number =
        body.paragraphs?.[0]?.paragraphStyle?.horizontalAlign ?? 0;
      el.style.color = ts.cl?.rgb ?? "#ffffff";
      el.style.fontSize = `${ts.fs ?? 12}px`;
      el.style.fontWeight = ts.bl ? "700" : "400";
      el.style.textAlign = ALIGN_MAP[alignIdx] ?? "left";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.overflow = "hidden";
      el.style.fontFamily =
        "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
      el.style.lineHeight = "1.3";
      el.style.padding = "0 4px";
      el.style.whiteSpace = "pre";
      el.textContent = (body.dataStream ?? "").replace(/\r\n$/, "");
    }

    container.appendChild(el);
  }

  document.body.appendChild(container);
  try {
    const canvas = await html2canvas(container, {
      width: pw,
      height: ph,
      scale: 2,
      useCORS: true,
      backgroundColor: null,
    });
    return canvas.toDataURL("image/png").split(",")[1];
  } finally {
    document.body.removeChild(container);
  }
};

export const BusinessReviewPage = () => {
  const { isCpa } = useRoleBasedView();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [review, setReview] = useState<BusinessReview | null>(null);
  const [snapshot, setSnapshot] = useState<object | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [exportBusinessReview] = useExportBusinessReviewMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const handleGenerated = (generated: BusinessReview) => {
    setReview(generated);
    setSnapshot(generated.snapshot ?? null);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    if (!snapshot) return;
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setSnapshot(review?.snapshot ?? null);
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
  };

  const handleDownloadPdf = async () => {
    if (!review || !snapshot) return;

    const { pages, pageOrder } = (snapshot as any)?.body ?? {};
    const pw: number = (snapshot as any)?.pageSize?.width ?? 960;
    const ph: number = (snapshot as any)?.pageSize?.height ?? 540;

    if (!pageOrder?.length) {
      alertToast({ message: "No slides found to export" });
      return;
    }

    setIsExporting(true);
    try {
      const graphs = await Promise.all(
        (pageOrder as string[]).map((pageId: string) =>
          captureSlide(pages[pageId], pw, ph)
        )
      );

      const { document_id } = await exportBusinessReview({
        entityId,
        reviewId: review.uuid,
        graphs,
      }).unwrap();

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();

      openLink(download_url);
      successToast({ message: "PDF downloaded successfully" });
    } catch {
      alertToast({ message: "Failed to export PDF" });
    } finally {
      setIsExporting(false);
    }
  };

  if (!review) {
    return (
      <div className="t-flex t-flex-col t-items-center t-justify-center t-h-full t-gap-4">
        <p className="t-text-body-sm t-text-text-30">
          Generate a Financial Review to get started.
        </p>
        {isCpa && (
          <Button customType="primary" onClick={() => setIsGenerateOpen(true)}>
            Generate Financial Review
          </Button>
        )}
        <GenerateReviewModal
          isOpen={isGenerateOpen}
          onClose={() => setIsGenerateOpen(false)}
          onCreated={handleGenerated}
        />
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-h-full">
      <div className="t-flex t-items-center t-justify-between t-px-6 t-py-3 t-border-b t-border-neutral-10">
        <div>
          <h2 className="t-text-subtitle t-text-text-100">{review.title}</h2>
          <p className="t-text-body-sm t-text-text-30">
            Generated{" "}
            {review.created_at
              ? dayjs(review.created_at).format("DD MMM YYYY")
              : "—"}{" "}
            by {review.created_by ?? "—"}
          </p>
        </div>
        <div className="t-flex t-items-center t-gap-2">
          {isEditing ? (
            <>
              <Button
                customType="ghost"
                size="small"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button
                customType="primary"
                size="small"
                onClick={handleSaveEdit}
              >
                Done
              </Button>
            </>
          ) : (
            <>
              <Button
                customType="secondary"
                size="small"
                isLoading={isExporting}
                onClick={handleDownloadPdf}
              >
                Download PDF
              </Button>
              {isCpa && (
                <Button
                  customType="secondary"
                  size="small"
                  onClick={handleStartEdit}
                >
                  Edit
                </Button>
              )}
              {isCpa && (
                <Button
                  customType="primary"
                  size="small"
                  onClick={() => setIsGenerateOpen(true)}
                >
                  Regenerate
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="t-flex-1 t-overflow-y-auto t-p-6 t-bg-surface-lighter-grey">
        <UniverSlidesViewer
          snapshot={snapshot}
          className="t-max-w-4xl t-mx-auto"
          editable={isEditing}
          onSnapshotChange={setSnapshot}
        />
      </div>

      <GenerateReviewModal
        isOpen={isGenerateOpen}
        onClose={() => setIsGenerateOpen(false)}
        onCreated={handleGenerated}
      />
    </div>
  );
};
